import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Search, Filter } from 'lucide-react';

export default function DataTable({ 
  columns, 
  data, 
  title,
  onRowClick,
  actions,
  searchable = true,
  searchPlaceholder = 'Search...'
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Filter data
  const filteredData = data.filter(item => {
    if (!searchTerm) return true;
    return columns.some(column => {
      const value = item[column.key];
      if (value === null || value === undefined) return false;
      return String(value).toLowerCase().includes(searchTerm.toLowerCase());
    });
  });

  // Sort data
  const sortedData = [...filteredData].sort((a, b) => {
    if (!sortConfig.key) return 0;
    const aValue = a[sortConfig.key];
    const bValue = b[sortConfig.key];
    if (aValue === null || aValue === undefined) return 1;
    if (bValue === null || bValue === undefined) return -1;
    if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  // Paginate data
  const totalPages = Math.ceil(sortedData.length / itemsPerPage);
  const paginatedData = sortedData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? 
      <ChevronUp className="w-3 h-3 sm:w-4 sm:h-4" /> : 
      <ChevronDown className="w-3 h-3 sm:w-4 sm:h-4" />;
  };

  // Get visible columns for mobile view (first 3-4 columns)
  const getMobileColumns = () => {
    // Show first 3 columns on mobile
    return columns.slice(0, 3);
  };

  return (
    <div className="card p-3 sm:p-6">
      {/* Header with title and search */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4 mb-4">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900">
          {title}
        </h3>
        <div className="flex flex-col xs:flex-row items-start xs:items-center gap-2 w-full sm:w-auto">
          {searchable && (
            <div className="relative w-full xs:w-48 sm:w-64">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder={searchPlaceholder}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 sm:py-2 border border-gray-200 rounded-lg text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          )}
          <button className="p-1.5 sm:p-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
            <Filter className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-600" />
          </button>
        </div>
      </div>

      {/* Desktop Table View */}
      <div className="hidden sm:block overflow-x-auto -mx-4 sm:mx-0">
        <div className="inline-block min-w-full align-middle">
          <table className="min-w-full">
            <thead>
              <tr className="border-b border-gray-200">
                {columns.map((column) => (
                  <th
                    key={column.key}
                    onClick={() => column.sortable !== false && handleSort(column.key)}
                    className={`text-left py-2.5 px-3 text-xs font-semibold text-gray-600 ${
                      column.sortable !== false ? 'cursor-pointer hover:text-gray-900' : ''
                    }`}
                  >
                    <div className="flex items-center space-x-1">
                      <span>{column.label}</span>
                      {column.sortable !== false && getSortIcon(column.key)}
                    </div>
                  </th>
                ))}
                {actions && actions.length > 0 && (
                  <th className="text-left py-2.5 px-3 text-xs font-semibold text-gray-600">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {paginatedData.length > 0 ? (
                paginatedData.map((item, index) => {
                  const visibleActions = actions?.filter(action => {
                    if (action.showWhen && !action.showWhen.includes(item.status)) {
                      return false;
                    }
                    if (action.hideWhen && action.hideWhen.includes(item.status)) {
                      return false;
                    }
                    return true;
                  });

                  return (
                    <tr
                      key={index}
                      onClick={() => onRowClick?.(item)}
                      className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                        onRowClick ? 'cursor-pointer' : ''
                      }`}
                    >
                      {columns.map((column) => (
                        <td key={column.key} className="py-2.5 px-3 text-sm text-gray-700 max-w-xs break-words">
                          {column.render ? column.render(item[column.key], item) : item[column.key] || '-'}
                        </td>
                      ))}
                      {actions && actions.length > 0 && (
                        <td className="py-2.5 px-3">
                          <div className="flex flex-wrap items-center gap-1">
                            {visibleActions.map((action, idx) => {
                              const isDisabled = typeof action.disabled === 'function' 
                                ? action.disabled(item) 
                                : action.disabled;
                              
                              let className = action.className || '';
                              if (typeof className === 'function') {
                                className = className(item);
                              }
                              
                              return (
                                <button
                                  key={idx}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (!isDisabled) {
                                      action.onClick(item);
                                    }
                                  }}
                                  disabled={isDisabled}
                                  className={`${className} p-1.5 rounded-lg transition-all duration-200 hover:scale-110 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed`}
                                  title={action.label}
                                >
                                  {action.icon}
                                </button>
                              );
                            })}
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan={columns.length + (actions && actions.length > 0 ? 1 : 0)}
                    className="py-8 text-center text-gray-500 text-sm"
                  >
                    No data available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile Card View */}
      <div className="sm:hidden space-y-3">
        {paginatedData.length > 0 ? (
          paginatedData.map((item, index) => {
            const mobileColumns = getMobileColumns();
            const visibleActions = actions?.filter(action => {
              if (action.showWhen && !action.showWhen.includes(item.status)) {
                return false;
              }
              if (action.hideWhen && action.hideWhen.includes(item.status)) {
                return false;
              }
              return true;
            });

            return (
              <div
                key={index}
                onClick={() => onRowClick?.(item)}
                className={`bg-white border border-gray-200 rounded-lg p-3 hover:bg-gray-50 transition-colors ${
                  onRowClick ? 'cursor-pointer' : ''
                }`}
              >
                {/* Mobile Card Content */}
                <div className="space-y-2">
                  {mobileColumns.map((column) => (
                    <div key={column.key} className="flex items-start justify-between">
                      <span className="text-xs font-medium text-gray-500">{column.label}:</span>
                      <span className="text-sm text-gray-900 text-right max-w-[60%] break-words">
                        {column.render ? column.render(item[column.key], item) : item[column.key] || '-'}
                      </span>
                    </div>
                  ))}
                  
                  {/* Show more info if there are more columns */}
                  {columns.length > 3 && (
                    <div className="text-xs text-gray-400 text-center pt-1">
                      +{columns.length - 3} more fields
                    </div>
                  )}
                  
                  {/* Actions on mobile */}
                  {actions && actions.length > 0 && visibleActions.length > 0 && (
                    <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100 mt-2">
                      {visibleActions.map((action, idx) => {
                        const isDisabled = typeof action.disabled === 'function' 
                          ? action.disabled(item) 
                          : action.disabled;
                        
                        let className = action.className || '';
                        if (typeof className === 'function') {
                          className = className(item);
                        }
                        
                        return (
                          <button
                            key={idx}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!isDisabled) {
                                action.onClick(item);
                              }
                            }}
                            disabled={isDisabled}
                            className={`${className} p-2 rounded-lg transition-all duration-200 hover:scale-110 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed`}
                            title={action.label}
                          >
                            {action.icon}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        ) : (
          <div className="text-center py-8 text-gray-500 text-sm">
            No data available
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex flex-col xs:flex-row items-center justify-between gap-3 mt-4 pt-4 border-t border-gray-200">
          <p className="text-xs sm:text-sm text-gray-600">
            Showing {((currentPage - 1) * itemsPerPage) + 1} to{' '}
            {Math.min(currentPage * itemsPerPage, sortedData.length)} of {sortedData.length} entries
          </p>
          <div className="flex space-x-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 border border-gray-200 rounded-lg text-xs sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="px-3 py-1 border border-gray-200 rounded-lg text-xs sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}